// U21 ikäkelpoisuuslaskuri - client-side
// Sama logiikka kuin backend/controllers/u21.js

const HT_YEAR = 112
const MAX_AGE_DAYS = 21 * HT_YEAR + 111 // 2463
const TOURNAMENT_LENGTH = 2 * HT_YEAR // 224

// Hattrickin viitekausi: kausi 40, päivä 1 = 2025-12-07
const REFERENCE_TOURNAMENT = 40
const REFERENCE_TOURNAMENT_DAY = 1
const REFERENCE_TOURNAMENT_START = new Date(2025, 11, 7)

/**
 * Laskee nykyisen turnauksen numeron ja päivän automaattisesti
 * @returns {{tournament: number, day: number}}
 */
function getCurrentTournamentAndDay() {
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const referenceDay = new Date(
    REFERENCE_TOURNAMENT_START.getFullYear(),
    REFERENCE_TOURNAMENT_START.getMonth(),
    REFERENCE_TOURNAMENT_START.getDate()
  )
  const daysSinceReference = Math.floor(
    (today - referenceDay) / (1000 * 60 * 60 * 24)
  )
  const tournament =
    REFERENCE_TOURNAMENT + Math.floor(daysSinceReference / TOURNAMENT_LENGTH)
  const day =
    REFERENCE_TOURNAMENT_DAY + (daysSinceReference % TOURNAMENT_LENGTH)
  return { tournament, day }
}

// Nykyinen turnauksen tila - LASKETAAN AUTOMAATTISESTI
const { tournament: CURRENT_TOURNAMENT, day: CURRENT_TOURNAMENT_DAY } =
  getCurrentTournamentAndDay()

const MMK_MATCHES = [
  { day: 19 },
  { day: 22 },
  { day: 26 },
  { day: 29 },
  { day: 33 },
  { day: 40 },
  { day: 47 },
  { day: 54 },
  { day: 61 },
  { day: 68 },
  { day: 75 },
  { day: 78 },
  { day: 82 },
]

const WC_ROUNDS = [
  { round: 1, days: [89, 92, 96, 103, 110, 117, 124, 131, 138, 145] },
  { round: 2, days: [152, 155, 159, 162, 166, 173] },
  { round: 3, days: [180, 183, 187] },
  { round: 4, days: [194, 197, 201, 208, 211, 215, 218, 222] },
]

const formatOptimalAgeDelta = daysYoungerThanOptimal => {
  if (daysYoungerThanOptimal > 0)
    return `${daysYoungerThanOptimal} päivää optimia nuorempi`
  if (daysYoungerThanOptimal < 0)
    return `${Math.abs(daysYoungerThanOptimal)} päivää optimia vanhempi`
  return 'optimi-ikäinen'
}

/**
 * Laskee U21-ikäkelpoisuuden pelaajalle ja palauttaa tiiviin tekstin
 * viimeisimmästä turnauksesta/kierroksesta.
 *
 * @param {number} ageYears - Pelaajan ikä vuosissa (HT)
 * @param {number} ageDays - Pelaajan ikä päivissä (HT)
 * @returns {object|null} { text, color } tai null jos yli-ikäinen / ei relevantti
 */
export const getU21Badge = (ageYears, ageDays) => {
  const currentAgeDays = ageYears * HT_YEAR + ageDays
  if (currentAgeDays > MAX_AGE_DAYS) return null

  // Etsi viimeisin turnaus jossa on ikäkelpoisia otteluita (MMK tai WC)
  let lastResult = null

  for (let t = 0; t < 10; t++) {
    const tournamentNumber = CURRENT_TOURNAMENT + t
    const offset = t * TOURNAMENT_LENGTH - CURRENT_TOURNAMENT_DAY

    // MMK kelpoisuus
    const mmkEligible = MMK_MATCHES.filter(m => {
      const ageAtMatch = currentAgeDays + offset + m.day
      return ageAtMatch >= 0 && ageAtMatch <= MAX_AGE_DAYS
    }).length

    // WC kierroksittain
    const wcRounds = WC_ROUNDS.map(r => {
      const eligible = r.days.filter(d => {
        const ageAtMatch = currentAgeDays + offset + d
        return ageAtMatch >= 0 && ageAtMatch <= MAX_AGE_DAYS
      }).length
      return { round: r.round, eligible, total: r.days.length }
    })

    const wcTotal = wcRounds.reduce((sum, r) => sum + r.eligible, 0)

    if (mmkEligible === 0 && wcTotal === 0) {
      // Tarkista onko tulevia otteluita
      const anyFuture =
        MMK_MATCHES.some(m => offset + m.day >= 0) ||
        WC_ROUNDS.some(r => r.days.some(d => offset + d >= 0))
      if (!anyFuture) break
      continue
    }

    // Etsi viimeisin kierros jossa voi pelata
    // Priorisoi WC > MMK, ja korkeampi kierros > matalampi
    const lastWcRound = [...wcRounds].reverse().find(r => r.eligible > 0)

    if (lastWcRound) {
      lastResult = {
        tournament: tournamentNumber,
        type: 'WC',
        round: lastWcRound.round,
        eligible: lastWcRound.eligible,
        total: lastWcRound.total,
      }
    } else if (mmkEligible > 0) {
      lastResult = {
        tournament: tournamentNumber,
        type: 'MMK',
        eligible: mmkEligible,
        total: MMK_MATCHES.length,
      }
    }
  }

  if (!lastResult) return null

  let text, color
  if (lastResult.type === 'WC') {
    text = `T${lastResult.tournament} WC R${lastResult.round} ${lastResult.eligible}/${lastResult.total}`
    color = lastResult.eligible === lastResult.total ? '#81c784' : '#ffb74d'
  } else {
    text = `T${lastResult.tournament} MMK ${lastResult.eligible}/${lastResult.total}`
    color = lastResult.eligible === lastResult.total ? '#4fc3f7' : '#ffb74d'
  }

  return { text, color }
}

/**
 * Hakee turnauksen tyypin ja numeron ehdokaslistan tiedoista.
 * Käyttää Competition ('CC'/'WC') ja Season-kenttiä suoraan.
 * Season-kenttä sisältää jo turnausnumeron (esim. 42).
 *
 * @param {object} candidate - Ehdokaslista-objekti (Competition, Season)
 * @returns {object|null} { type: 'CC'|'WC', tournament: number } tai null
 */
export const getTournamentFromCandidate = candidate => {
  if (!candidate || !candidate.Competition || !candidate.Season) return null
  return {
    type: candidate.Competition,
    tournament: candidate.Season,
  }
}

/**
 * Laskee pelaajan ikäkelpoisuuden tiettyyn turnaukseen (MMK tai WC).
 *
 * @param {number} ageYears - Pelaajan ikä vuosissa (HT)
 * @param {number} ageDays - Pelaajan ikä päivissä (HT)
 * @param {string} tournamentType - 'CC' (=MMK) tai 'WC'
 * @param {number} tournamentNumber - Turnauksen numero (esim. 42)
 * @returns {object|null} { text, color, tooltip }
 */
/**
 * Laskee seuraavan kilpailun kelpoisuustekstin tooltipiin.
 * CC → saman turnauksen WC, WC → seuraavan turnauksen CC.
 */
const getNextCompetitionTooltip = (
  currentAgeDays,
  tournamentType,
  tournamentNumber
) => {
  if (tournamentType === 'CC') {
    // Seuraava on saman turnauksen WC
    const wcOffset =
      (tournamentNumber - CURRENT_TOURNAMENT) * TOURNAMENT_LENGTH -
      CURRENT_TOURNAMENT_DAY
    const wcRounds = WC_ROUNDS.map(r => {
      const eligible = r.days.filter(d => {
        const ageAtMatch = currentAgeDays + wcOffset + d
        return ageAtMatch >= 0 && ageAtMatch <= MAX_AGE_DAYS
      }).length
      return { round: r.round, eligible, total: r.days.length }
    })
    const lastRound = [...wcRounds].reverse().find(r => r.eligible > 0)
    if (lastRound) {
      return `WC R${lastRound.round} ${lastRound.eligible}/${lastRound.total}`
    }
    return null
  } else {
    // Seuraava on seuraavan turnauksen CC (MMK)
    const nextTournament = tournamentNumber + 1
    const ccOffset =
      (nextTournament - CURRENT_TOURNAMENT) * TOURNAMENT_LENGTH -
      CURRENT_TOURNAMENT_DAY
    const eligible = MMK_MATCHES.filter(m => {
      const ageAtMatch = currentAgeDays + ccOffset + m.day
      return ageAtMatch >= 0 && ageAtMatch <= MAX_AGE_DAYS
    }).length
    if (eligible > 0) {
      return `T${nextTournament} CC ${eligible}/${MMK_MATCHES.length}`
    }
    return null
  }
}

export const getU21TournamentEligibility = (
  ageYears,
  ageDays,
  tournamentType,
  tournamentNumber
) => {
  const currentAgeDays = ageYears * HT_YEAR + ageDays
  if (currentAgeDays > MAX_AGE_DAYS)
    return { text: '❌', color: '#e57373', tooltip: 'Liian vanha' }

  const tournamentsAhead = tournamentNumber - CURRENT_TOURNAMENT
  const offset = tournamentsAhead * TOURNAMENT_LENGTH - CURRENT_TOURNAMENT_DAY

  // Laske sekä CC että WC kelpoisuudet
  const ccEligible = MMK_MATCHES.filter(m => {
    const ageAtMatch = currentAgeDays + offset + m.day
    return ageAtMatch >= 0 && ageAtMatch <= MAX_AGE_DAYS
  }).length

  const wcRounds = WC_ROUNDS.map(r => {
    const eligible = r.days.filter(d => {
      const ageAtMatch = currentAgeDays + offset + d
      return ageAtMatch >= 0 && ageAtMatch <= MAX_AGE_DAYS
    }).length
    return { round: r.round, eligible, total: r.days.length }
  })

  const wcTotalEligible = wcRounds.reduce((sum, r) => sum + r.eligible, 0)
  const wcTotalMatches = wcRounds.reduce((sum, r) => sum + r.total, 0)
  const lastWcRound = [...wcRounds].reverse().find(r => r.eligible > 0)
  const wcTooltipLine = (() => {
    if (!lastWcRound) return null
    const roundDefinition = WC_ROUNDS.find(r => r.round === lastWcRound.round)
    const displayedLastMatchDay = roundDefinition
      ? roundDefinition.days[roundDefinition.days.length - 1]
      : lastWcRound.round
    const optimalAgeDays = MAX_AGE_DAYS - (offset + displayedLastMatchDay)
    const daysYoungerThanOptimal = optimalAgeDays - currentAgeDays
    return (
      `WC R${lastWcRound.round} ${lastWcRound.eligible}/${lastWcRound.total}, ` +
      formatOptimalAgeDelta(daysYoungerThanOptimal)
    )
  })()

  // Prioriteetti: näytä WC CC-listalla vain jos kelpoisuutta on riittävästi.
  // Jos paras kelpoisuus on vain R1 4/10, pidä näkymä CC:nä (13/13).
  const shouldShowWc =
    tournamentType === 'WC' ||
    (lastWcRound &&
      (lastWcRound.round > 1 ||
        lastWcRound.eligible >= 5 ||
        wcTotalEligible >= 5))
  const showWc = tournamentType === 'CC' ? shouldShowWc : true

  if (showWc) {
    // WC - näytä kierroksittain
    if (wcTotalEligible === 0)
      return {
        text: '0/' + wcTotalMatches,
        color: '#e57373',
        tooltip: `WC 0/${wcTotalMatches}, ei voi pelata yhtään ottelua`,
      }

    // Laske optimi näytettävän kierroksen viimeiselle ottelulle
    const roundDefinition = WC_ROUNDS.find(r => r.round === lastWcRound.round)
    const displayedLastMatchDay = roundDefinition
      ? roundDefinition.days[roundDefinition.days.length - 1]
      : lastWcRound.round
    const optimalAgeDays = MAX_AGE_DAYS - (offset + displayedLastMatchDay)
    const daysYoungerThanOptimal = optimalAgeDays - currentAgeDays
    const tooltipBase = wcTooltipLine

    if (wcTotalEligible === wcTotalMatches) {
      let tooltip = tooltipBase
      // Jos 30+ päivää nuorempi, näytä seuraavan kilpailun tiedot
      if (daysYoungerThanOptimal >= 30) {
        const nextInfo = getNextCompetitionTooltip(
          currentAgeDays,
          'WC',
          tournamentNumber
        )
        if (nextInfo) tooltip += `\n${nextInfo}`
      }
      return {
        text: `R${lastWcRound.round} ${lastWcRound.eligible}/${lastWcRound.total}`,
        color: '#81c784',
        tooltip,
      }
    }

    // Näytä viimeisen kierroksen tiedot
    return {
      text: `R${lastWcRound.round} ${lastWcRound.eligible}/${lastWcRound.total}`,
      color: lastWcRound.eligible === lastWcRound.total ? '#81c784' : '#ffb74d',
      tooltip: tooltipBase,
    }
  } else {
    // CC / MMK
    const ccTotal = MMK_MATCHES.length

    if (ccEligible === 0)
      return {
        text: '0/' + ccTotal,
        color: '#e57373',
        tooltip: wcTooltipLine
          ? `CC 0/${ccTotal}, ei voi pelata yhtään ottelua\n${wcTooltipLine}`
          : `CC 0/${ccTotal}, ei voi pelata yhtään ottelua`,
      }

    // Laske optimi: montako päivää nuorempi kuin optimi-ikä
    // Optimi = max ikä jolla voi pelata viimeisen ottelun
    const lastMatchDay = MMK_MATCHES[MMK_MATCHES.length - 1].day
    const optimalAgeDays = MAX_AGE_DAYS - (offset + lastMatchDay)
    const daysYoungerThanOptimal = optimalAgeDays - currentAgeDays
    const tooltipBase =
      `CC ${ccEligible}/${ccTotal}, ` +
      formatOptimalAgeDelta(daysYoungerThanOptimal)

    if (ccEligible === ccTotal) {
      let tooltip = tooltipBase
      if (wcTooltipLine) tooltip += `\n${wcTooltipLine}`
      // Jos 30+ päivää nuorempi, näytä seuraavan kilpailun tiedot
      if (daysYoungerThanOptimal >= 30) {
        const nextInfo = getNextCompetitionTooltip(
          currentAgeDays,
          'CC',
          tournamentNumber
        )
        if (nextInfo) tooltip += `\n${nextInfo}`
      }
      return { text: `${ccEligible}/${ccTotal}`, color: '#81c784', tooltip }
    }

    return {
      text: `${ccEligible}/${ccTotal}`,
      color: '#ffb74d',
      tooltip: wcTooltipLine ? `${tooltipBase}\n${wcTooltipLine}` : tooltipBase,
    }
  }
}
